import React, { memo } from "react"
import { Handle, NodeProps, Position } from "@xyflow/react"

import { BranchNodeData } from "lib/types/workflow"

import {
  branchCaseHandle,
  HANDLE_BRANCH_OTHERWISE,
  HANDLE_INPUT
} from "../../nodeKind"
import { BRANCH_OTHERWISE_LABEL } from "../../branchKind"

import {
  CasePill,
  CaseSummary,
  InlineAddCaseButton,
  NodeBody,
  NodeContainer,
  NodeHeader,
  OtherwisePill
} from "./BranchNode.styled"
import {
  FailedMessageBlock,
  StatusBadge,
  statusIcon
} from "./ExecutionOverlay.styled"
import TerminalAddButton from "./TerminalAddButton"

/** Evenly distribute N source handles across the bottom edge: each handle
 *  sits at the centre of its 1/(N+1) horizontal slot. With 3 outcomes you
 *  get 25%, 50%, 75%; with 4 you get 20%, 40%, 60%, 80%, etc. */
const slotLeft = (index: number, count: number): string =>
  `${((index + 1) / (count + 1)) * 100}%`

const BranchNode = memo(({ id, data }: NodeProps) => {
  const d = data as BranchNodeData
  const terminals = d._terminalHandles ?? []
  const status = d._executionStatus
  const cases = d.cases ?? []
  const totalOutcomes = cases.length + 1

  return (
    <NodeContainer $skipped={status === "skipped"} $caseCount={cases.length}>
      <Handle type="target" position={Position.Top} id={HANDLE_INPUT} />
      <NodeHeader>
        <span>Branch</span>
        {d._onAddCase && (
          <InlineAddCaseButton
            type="button"
            aria-label="Add case"
            className="nodrag nopan"
            onClick={(e) => {
              e.stopPropagation()
              d._onAddCase?.(id)
            }}
          />
        )}
      </NodeHeader>
      <NodeBody>{d.label}</NodeBody>
      <CaseSummary>
        {cases.length} {cases.length === 1 ? "case" : "cases"} + otherwise
      </CaseSummary>
      {d._failedMessage && (
        <FailedMessageBlock>{d._failedMessage}</FailedMessageBlock>
      )}

      {cases.map((c, i) => {
        const handleId = branchCaseHandle(c.id)
        const left = slotLeft(i, totalOutcomes)
        return (
          <React.Fragment key={c.id}>
            <Handle
              type="source"
              position={Position.Bottom}
              id={handleId}
              style={{ left }}
            />
            <CasePill style={{ left }}>{c.label || `Case ${i + 1}`}</CasePill>
            {terminals.includes(handleId) && d._onAddAtTerminal && (
              <TerminalAddButton
                left={left}
                onClick={() => d._onAddAtTerminal?.(id, handleId)}
                highlighted={d._activeTerminalDropHandle === handleId}
              />
            )}
          </React.Fragment>
        )
      })}

      {/* Otherwise slot — always the rightmost outcome. */}
      {(() => {
        const left = slotLeft(cases.length, totalOutcomes)
        return (
          <>
            <Handle
              type="source"
              position={Position.Bottom}
              id={HANDLE_BRANCH_OTHERWISE}
              style={{ left }}
            />
            <OtherwisePill style={{ left }}>
              {BRANCH_OTHERWISE_LABEL}
            </OtherwisePill>
            {terminals.includes(HANDLE_BRANCH_OTHERWISE) &&
              d._onAddAtTerminal && (
                <TerminalAddButton
                  left={left}
                  onClick={() =>
                    d._onAddAtTerminal?.(id, HANDLE_BRANCH_OTHERWISE)
                  }
                  highlighted={
                    d._activeTerminalDropHandle === HANDLE_BRANCH_OTHERWISE
                  }
                />
              )}
          </>
        )
      })()}

      {status && (
        <StatusBadge $status={status}>{statusIcon(status)}</StatusBadge>
      )}
    </NodeContainer>
  )
})

BranchNode.displayName = "BranchNode"

export default BranchNode
